import { ArrowRightAlt } from "@ellaandil/ui";
import { clsx } from "@trydig/ui";
import type { ComponentProps } from "react";
import { twMerge } from "tailwind-merge";
import { Container } from "@/components/ui/Container";
import { Link } from "@/components/ui/Link";
import { ProductCard } from "@/components/ui/ProductCard";
import { FeaturedCollectionCarousel } from "@/components/ui/sections/_lib/FeaturedCollectionCarousel";
import { Typography } from "@/components/ui/Typography";
import type { ProductsRoute } from "@/data/routes/products.route";
import { Link as NextLink } from "@/i18n/routing";
import { isDarkMode } from "@/lib/utils/isDarkMode";
import { Reveal } from "../Reveal";

export interface FeaturedCollectionHighlightedProps extends ComponentProps<"section"> {
	caption?: string;
	heading?: string;
	description?: string;
	linkLabel?: string;
	linkHref?: string;
	products?: ProductsRoute["exploration"]["products"];
	bg?: string;
}

/**
 * FeaturedCollectionHighlighted — full-bleed highlight block with heading +
 * description + CTA on the left and a products carousel on the right.
 * Background color is set via `bg`.
 */
export const FeaturedCollectionHighlighted = ({
	caption,
	heading,
	description,
	linkLabel,
	linkHref,
	products,
	bg,
	className,
	...rest
}: FeaturedCollectionHighlightedProps) => {
	const darkMode = isDarkMode(bg);

	const items = (products ?? []).map((product, i) => (
		<Reveal key={product.id} animation="fade-bottom" delay={100 * (i + 1)}>
			<ProductCard key={product.id} product={product} size="medium" darkMode={darkMode} />
		</Reveal>
	));
	const hasLink = Boolean(linkLabel && linkHref);
	const hasHeading = Boolean(caption || heading);
	const desktopCarousel = items.length > 2;

	return (
		<section {...rest}>
			<Container className="py-grid-section-vertical-lg">
				<div
					className={twMerge(
						"flex w-full flex-col gap-grid-margin-xs overflow-hidden p-grid-margin-xs",
						className,
					)}
					style={{ backgroundColor: bg ? bg : undefined }}
				>
					<Reveal animation="draw-left" delay={100}>
						<div
							className={clsx("h-px w-full bg-border-dark", darkMode && "bg-border-light")}
							aria-hidden
						/>
					</Reveal>
					<div className="flex w-full flex-col gap-grid-section-vertical-md lg:flex-row lg:items-stretch lg:gap-grid-margin-md">
						<div className="flex min-w-px flex-1 flex-col justify-between gap-grid-section-vertical-md py-grid-section-vertical-md">
							{hasHeading && (
								<div
									className={clsx(
										"flex w-full flex-col gap-grid-component-spacing-md text-text-dark-text-1",
										darkMode && "text-text-light-text-1",
									)}
								>
									{heading && (
										<Typography variant="heading-display-md" as="h2">
											{heading}
										</Typography>
									)}
									{caption && (
										<Typography variant="label-sm-reg" as="p" className="leading-[1.15]">
											{caption}
										</Typography>
									)}
								</div>
							)}
							{(description || hasLink) && (
								<div className="flex w-full flex-col gap-grid-component-spacing-2xl">
									{description && (
										<Typography
											variant="body-sm-reg"
											as="p"
											className={clsx(
												"text-text-dark-text-1",
												darkMode && "text-text-light-text-1",
											)}
										>
											{description}
										</Typography>
									)}
									{hasLink && linkHref && (
										<Link
											as={NextLink}
											href={linkHref}
											variant="secondary"
											size="medium"
											rightIcon={ArrowRightAlt}
											className={clsx(
												"w-full justify-between uppercase",
												darkMode && "text-text-light-text-1",
											)}
										>
											{linkLabel}
										</Link>
									)}
								</div>
							)}
						</div>
						<div className="flex min-w-px flex-1 py-grid-section-vertical-md">
							<FeaturedCollectionCarousel
								items={items}
								alwaysActive={desktopCarousel}
								desktopLayout="pair"
								className="w-full"
								darkMode={darkMode}
							/>
						</div>
					</div>
					<Reveal animation="draw-right" delay={100}>
						<div
							className={clsx("h-px w-full bg-border-dark", darkMode && "bg-border-light")}
							aria-hidden
						/>
					</Reveal>
				</div>
			</Container>
		</section>
	);
};
